import { AccessibilityContext, FONT_SIZE_OPTIONS, type FontSize } from '@/contexts/accessibility.context'
import { useCallback, useEffect, useState, type ReactNode } from 'react'

export const FONT_SIZE_PREFERENCE_KEY = 'accessibility-font-size'
export const HIGH_CONTRAST_PREFERENCE_KEY = 'accessibility-high-contrast'

interface AccessibilityProviderProps {
  children: ReactNode
}

/**
 * Provider for Accessibility settings
 * Handles font size and high contrast mode globally
 * Settings are applied immediately and synced with backend via UserPreferenceProvider
 */
export const AccessibilityProvider = ({ children }: AccessibilityProviderProps) => {
  const [fontSize, setFontSizeState] = useState<FontSize>('medium')
  const [highContrastMode, setHighContrastModeState] = useState<boolean>(false)

  // Apply font size to document
  useEffect(() => {
    const root = document.documentElement
    const option = FONT_SIZE_OPTIONS.find((opt) => opt.value === fontSize)
    const scale = option?.scale || 1

    // Set CSS custom property for font size scale
    root.style.setProperty('--font-size-scale', scale.toString())

    // Apply base font size (16px * scale)
    root.style.fontSize = `${16 * scale}px`

    // Remove old font size classes and add new one
    root.classList.remove('font-size-small', 'font-size-medium', 'font-size-large', 'font-size-extra-large')
    root.classList.add(`font-size-${fontSize}`)
  }, [fontSize])

  // Apply high contrast mode to document
  useEffect(() => {
    const root = document.documentElement

    if (highContrastMode) root.classList.add('high-contrast')
    else root.classList.remove('high-contrast')
  }, [highContrastMode])

  const setFontSize = useCallback((size: FontSize) => {
    setFontSizeState(size)
  }, [])

  const setHighContrastMode = useCallback((enabled: boolean) => {
    setHighContrastModeState(enabled)
  }, [])

  return (
    <AccessibilityContext.Provider
      value={{
        fontSize,
        setFontSize,
        highContrastMode,
        setHighContrastMode,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  )
}
